import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
// Using API types for ServiceStore entities
import type { Payment } from "types/common/ServiceStoreTypes";
// Keep local types for UI-specific data
import { PaymentMethod, PaymentStatus } from "../types";

// Formatted payment interface for display
export interface FormattedPayment extends Omit<Payment, "paymentType"> {
  formattedAmount: string;
  formattedDate: string;
  paymentType: "Received" | "Sent" | "Unknown";
}

// Payment form data interface
export interface PaymentFormData {
  id?: string;
  paymentNumber: string;
  type: "received" | "sent"; // Keep legacy for form compatibility
  paymentType?: "customer_payment" | "supplier_payment"; // New field
  amount: number;
  paymentDate: string; // YYYY-MM-DD
  paymentMethod: PaymentMethod;
  reference?: string;
  referenceNumber?: string; // New field
  notes?: string;
  customerId?: string | number | null;
  supplierId?: string | number | null;
  invoiceId?: string | number | null;
  billId?: string | number | null;
  status: PaymentStatus;
}

class PaymentsStore {
  rootStore: RootStore;

  // Observables - Only UI state
  isFormOpen = false;
  selectedPaymentId: string | null = null;
  formData: PaymentFormData;
  isEditMode = false;
  selectedPaymentIds: string[] = [];
  filterType: "all" | "received" | "sent" = "all";
  filterMethod: "all" | string = "all"; // "all" | PaymentMethod
  dateRange = {
    start: "",
    end: "",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.formData = this.getInitialFormData();

    makeAutoObservable(this, {
      rootStore: false,
    });
  }

  // Actions
  setFilterType = (type: "all" | "received" | "sent") => {
    this.filterType = type;
  };

  setFilterMethod = (method: "all" | string) => {
    this.filterMethod = method;
  };

  setDateRange = (start: string, end: string) => {
    this.dateRange = { start, end };
  };

  openCreateForm = (type?: "received" | "sent") => {
    this.formData = this.getInitialFormData();
    if (type) {
      this.formData.type = type;
    }
    this.isEditMode = false;
    this.isFormOpen = true;
    this.selectedPaymentId = null;
  };

  openEditForm = (paymentId: string) => {
    const payment = this.paymentsStore.entities.find((p: any) => p.id === paymentId) as unknown as Payment;
    if (!payment) return;

    // Map paymentType to legacy type for form compatibility
    const getLegacyType = (paymentType: string) => {
      if (paymentType === "customer_payment") return "received";
      if (paymentType === "supplier_payment") return "sent";
      return "received"; // default
    };

    this.formData = {
      id: payment.id,
      paymentNumber: payment.paymentNumber,
      type: (payment as any).type || getLegacyType(payment.paymentType),
      paymentType: payment.paymentType,
      amount: payment.amount,
      paymentDate: payment.paymentDate,
      paymentMethod: payment.paymentMethod,
      reference: (payment as any).reference || payment.referenceNumber || "",
      referenceNumber: payment.referenceNumber,
      notes: payment.notes || "",
      customerId: (payment as any).customerId,
      supplierId: (payment as any).supplierId,
      invoiceId: payment.invoiceId,
      billId: payment.billId,
      status: payment.status,
    };
    this.isEditMode = true;
    this.isFormOpen = true;
    this.selectedPaymentId = paymentId;
  };

  closeForm = () => {
    this.isFormOpen = false;
    this.isEditMode = false;
    this.selectedPaymentId = null;
    this.formData = this.getInitialFormData();
  };

  updateFormData = (
    field: keyof PaymentFormData,
    value: string | number | boolean | Date
  ) => {
    this.formData = { ...this.formData, [field]: value };
  };

  savePayment = async () => {
    try {
      if (this.isEditMode && this.formData.id) {
        await this.paymentsStore.patch(parseInt(this.formData.id), this.formData);
      } else {
        await this.paymentsStore.create(this.formData);
      }

      this.closeForm();
    } catch (error) {
      console.error("Failed to save payment:", error);
      throw error;
    }
  };

  deletePayment = async (paymentId: string) => {
    try {
      await this.paymentsStore.remove(parseInt(paymentId));
    } catch (error) {
      console.error("Failed to delete payment:", error);
      throw error;
    }
  };

  togglePaymentSelection = (paymentId: string) => {
    if (this.selectedPaymentIds.includes(paymentId)) {
      this.selectedPaymentIds = this.selectedPaymentIds.filter(
        (id) => id !== paymentId
      );
    } else {
      this.selectedPaymentIds = [...this.selectedPaymentIds, paymentId];
    }
  };

  selectAllPayments = () => {
    this.selectedPaymentIds = this.filteredPayments.map((payment) => payment.id);
  };

  clearSelection = () => {
    this.selectedPaymentIds = [];
  };

  bulkDelete = async () => {
    try {
      const deletePromises = this.selectedPaymentIds.map((paymentId) =>
        this.paymentsStore.remove(parseInt(paymentId))
      );
      await Promise.all(deletePromises);
      this.clearSelection();
    } catch (error) {
      console.error("Failed to bulk delete payments:", error);
      throw error;
    }
  };

  loadPayments = async () => {
    try {
      await this.paymentsStore.find({});
    } catch (error) {
      console.error("Failed to load payments:", error);
      throw error;
    }
  };

  // Actions

  private getInitialFormData(): PaymentFormData {
    return {
      paymentNumber: `PAY-${Date.now()}`,
      type: "received",
      amount: 0,
      paymentDate: new Date().toISOString().split("T")[0],
      paymentMethod: PaymentMethod.CASH,
      reference: "",
      notes: "",
      status: PaymentStatus.PENDING,
    };
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }

  // Computed values
  get paymentsStore() {
    return this.rootStore.paymentsStore as ServiceStore;
  }

  get payments(): Payment[] {
    return (this.paymentsStore.entities as unknown as Payment[]) || [];
  }

  get selectedPayment(): Payment | null {
    return this.selectedPaymentId ? this.paymentsStore.entities.find((p: any) => p.id === this.selectedPaymentId) as unknown as Payment : null;
  }

  get isLoading(): boolean {
    return this.paymentsStore.isServicePending;
  }

  get filteredPayments(): Payment[] {
    let filtered = this.payments;
    console.log("Filtering payments:", filtered);

    if (this.filterType !== "all") {
      // Map paymentType to our internal type system for filtering
      const getPaymentType = (payment: Payment) => {
        if (payment.paymentType === "customer_payment") return "received";
        if (payment.paymentType === "supplier_payment") return "sent";
        // Fallback to legacy type field if it exists
        return (payment as any).type || "sent";
      };

      filtered = filtered.filter(
        (payment) => getPaymentType(payment) === this.filterType
      );
    }

    if (this.filterMethod !== "all") {
      filtered = filtered.filter(
        (payment) => payment.paymentMethod === this.filterMethod
      );
    }

    if (this.dateRange.start) {
      filtered = filtered.filter(
        (payment) => payment.paymentDate >= this.dateRange.start
      );
    }

    if (this.dateRange.end) {
      filtered = filtered.filter(
        (payment) => payment.paymentDate <= this.dateRange.end
      );
    }

    return filtered;
  }

  get paymentSummary() {
    console.log("paymentSummary payments:", this.payments);

    // Map paymentType to our internal type system
    const getPaymentType = (payment: Payment) => {
      if (payment.paymentType === "customer_payment") return "received";
      if (payment.paymentType === "supplier_payment") return "sent";
      // Fallback to legacy type field if it exists
      return payment.type || "sent";
    };

    const received = this.payments
      .filter((p) => getPaymentType(p) === "received")
      .reduce((sum, p) => sum + p.amount, 0);

    const sent = this.payments
      .filter((p) => getPaymentType(p) === "sent")
      .reduce((sum, p) => sum + p.amount, 0);

    const thisMonth = new Date().toISOString().substring(0, 7);
    const thisMonthReceived = this.payments
      .filter(
        (p) =>
          getPaymentType(p) === "received" &&
          p.paymentDate.startsWith(thisMonth)
      )
      .reduce((sum, p) => sum + p.amount, 0);

    const thisMonthSent = this.payments
      .filter(
        (p) =>
          getPaymentType(p) === "sent" && p.paymentDate.startsWith(thisMonth)
      )
      .reduce((sum, p) => sum + p.amount, 0);

    return {
      totalReceived: received,
      totalSent: sent,
      netCashFlow: received - sent,
      thisMonthReceived,
      thisMonthSent,
      thisMonthNet: thisMonthReceived - thisMonthSent,
    };
  }

  get paymentsByMethod() {
    const methods = this.payments.reduce((acc, payment) => {
      const method = payment.paymentMethod;
      if (!acc[method]) {
        acc[method] = { count: 0, total: 0 };
      }
      acc[method].count++;
      acc[method].total += payment.amount;
      return acc;
    }, {} as Record<string, { count: number; total: number }>);

    return methods;
  }

  get formattedPayments(): FormattedPayment[] {
    return this.payments.map((payment) => ({
      ...payment,
      formattedAmount: this.formatCurrency(payment.amount),
      formattedDate: this.formatDate(payment.paymentDate),
      // Map paymentType to display-friendly values
      paymentType:
        payment.paymentType === "customer_payment"
          ? "Received"
          : payment.paymentType === "supplier_payment"
            ? "Sent"
            : "Unknown", // Simplified fallback
    }));
  }
}

export default PaymentsStore;
